import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { authService } from '../../services/api';
import axios from 'axios';
import AuthCard from './AuthCard';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

const MagicLinkActivation = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  // Set once the link is checked, so the logo is looked up in the link's workspace (S-4, agreed 18 Oct).
  const [linkChecked, setLinkChecked] = useState(false);
  const mounted = useRef(true);

  /* ── Magic-link verification ── */
  useEffect(() => {
    const handleMagicLogin = async () => {
      try {
        const response = await authService.magicLogin(token);
        if (!mounted.current) return;

        if (response.data.success) {
          localStorage.setItem('isAuthenticated', 'true');
          if (response.data.user) {
            localStorage.setItem('userData', JSON.stringify(response.data.user));
          }
          setStatus('success');
          // Small delay so the user sees the success state before redirect
          setTimeout(() => { window.location.href = '/dashboard'; }, 5000);
        } else {
          setErrorMsg(response.data.message || 'Magic link login failed.');
          setStatus('error');
        }
      } catch (error) {
        if (!mounted.current) return;
        setErrorMsg(error.response?.data?.message || 'Failed to login with magic link.');
        // A refusal (403) gets its own card below (K11 pt 2, S-14, S-6.1 pt 5).
        setStatus(error.response?.status === 403 && error.response?.data?.message ? 'refused' : 'error');
      } finally {
        if (mounted.current) setLinkChecked(true);
      }
    };

    handleMagicLogin();
    return () => { mounted.current = false; };
  }, [token]);

    /* ── Logo fetch ── */
    // After the link's check, which gives the page the link's workspace, so the logo is that workspace's in any browser
    // (S-4, agreed 18 Oct). Removed 18 Oct 2026 (S-4): looking it up before, which gave the browser's last workspace.
    useEffect(() => {
        if (!linkChecked) return;
        const fetchLogo = async () => {
            try {
                const res = await axios.get('/api/settings/logo'); 
                console.log("Logo fetch response:", res.data);
                if (res.data.success && res.data.logoUrl) {
                    setLogoUrl(res.data.logoUrl);
                }
                else
                {
                    setLogoUrl("/default-logo.png"); // Set to default logo if not provided
                }
            } catch (err) {
                console.error("Failed to fetch logo", err);
            }
        };
        
        fetchLogo();
    }, [linkChecked]);

  /* ────────────────────────────────────────────────── Loading ── */
  if (status === 'loading') {
    return (
      <AuthCard logoUrl={logoUrl} badge="Customer account">
        <div className="ac-body">
          <div className="ac-spinner">
            <CircularProgress size={28} sx={{ color: 'var(--ti)' }} />
            <span className="ac-spinner-text">Logging you in…</span>
          </div>
        </div>
      </AuthCard>
    );
  }

  /* ────────────────────────────────────────────────── Success ── */
  if (status === 'success') {
    return (
      <AuthCard logoUrl={logoUrl} badge="Customer account">
        <div className="ac-body">            
            <div className="ac-title">Welcome, {JSON.parse(localStorage.getItem('userData')).name}</div>
            <div className="ac-sub">Your admin set up your learning account. No password needed.</div>
            <div className="ac-box ac-box-success">
                <div className="ac-box-title">Account active</div>
                <div className="ac-box-sub">Taking you to your dashboard…</div>
            </div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }

  /* ─────────────────────────────────────────────────── Refusal ── */
  // A refusal (403: a switched-off workspace, an ended free trial, a free trial at its user limit, or a Deactivated
  // account) shows its own words as the agreed two lines, as on the self-signup link's page (commit 81): the first
  // sentence, then the rest (K11 pt 2, S-14, S-6.1 pt 5). Removed 18 Oct 2026 (K11 pt 2): "This link is no longer valid"
  // for a refusal, which is untrue there (a refused link is left as it was), with the reason in a box under it.
  if (status === 'refused') {
    const cut = errorMsg.indexOf('. ');
    return (
      <AuthCard logoUrl={logoUrl} badge="Customer account">
        <div className="ac-top">
          <div className="ac-icon ac-icon-error">
            <span style={{ fontSize: '24px' }}><CloseOutlinedIcon /></span>
          </div>
          <div className="ac-title">{cut > 0 ? errorMsg.slice(0, cut + 1) : errorMsg}</div>
          {cut > 0 && <div className="ac-sub">{errorMsg.slice(cut + 2)}</div>}
        </div>
        <div className="ac-body">
          <button
            className="ac-btn-primary"
            onClick={() => { window.location.href = '/login'; }}
          >
            Back to login
          </button>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }

  /* ─────────────────────────────────────────────────── Error ── */
  return (
    <AuthCard logoUrl={logoUrl} badge="Customer account">
      <div className="ac-body">
        <div className="ac-title">This link is no longer valid</div>
        <div className="ac-sub">It may have expired or already been used. Links are single-use and time-limited for security.</div>
        <div className="ac-box ac-box-error">
          <div className="ac-box-title">Verification failed</div>
          <div className="ac-box-sub">{errorMsg}</div>
        </div>
        <button
          className="ac-btn-primary"
          onClick={() => { window.location.href = '/login'; }}
        >
          Back to login
        </button>
        
      </div>
      <div className="ac-footer" />
    </AuthCard>
  );
};

export default MagicLinkActivation;
